import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CalendarIcon } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AssignShiftDialog = ({ open, onOpenChange, employees, onSuccess }) => {
  const { t } = useLanguage();
  const [employeeId, setEmployeeId] = useState("");
  const [mode, setMode] = useState("single"); // "single" or "range"
  const [date, setDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [excludedDays, setExcludedDays] = useState([]);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");

  const dayNames = [t("monday") || "Mon", t("tuesday") || "Tue", t("wednesday") || "Wed", t("thursday") || "Thu", t("friday") || "Fri", t("saturday") || "Sat", t("sunday") || "Sun"];

  const toggleExcludedDay = (dayIndex) => {
    setExcludedDays(prev => 
      prev.includes(dayIndex) ? prev.filter(d => d !== dayIndex) : [...prev, dayIndex]
    );
  };

  const handleSubmit = async () => {
    if (!employeeId) { 
      toast.error("Select an employee"); 
      return; 
    }
    try {
      const body = {
        user_id: employeeId, 
        start_time: startTime, 
        end_time: endTime
      };
      
      if (mode === "range") {
        body.start_date = format(date, 'yyyy-MM-dd');
        body.end_date = format(endDate, 'yyyy-MM-dd');
        body.exclude_days = excludedDays.length > 0 ? excludedDays : null;
      } else {
        body.date = format(date, 'yyyy-MM-dd');
      }
      
      const res = await fetch(`${API_URL}/api/shifts/assign`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', 
        body: JSON.stringify(body)
      });
      if (res.ok) { 
        const data = await res.json();
        toast.success(data.message || "Shift(s) assigned"); 
        onOpenChange(false);
        setExcludedDays([]);
        if (onSuccess) onSuccess();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed");
      }
    } catch (e) { 
      toast.error("Failed"); 
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("assignShift") || "Assign Shift"}</DialogTitle>
          <DialogDescription>Assign a shift to an employee</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t("employee") || "Employee"}</Label>
            <Select value={employeeId} onValueChange={setEmployeeId}>
              <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((emp) => (
                  <SelectItem key={emp.user_id} value={emp.user_id}>{emp.display_name || emp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Single/Range Toggle */}
          <div className="flex gap-2">
            <Button size="sm" variant={mode === "single" ? "default" : "outline"} onClick={() => setMode("single")}>Single Day</Button>
            <Button size="sm" variant={mode === "range" ? "default" : "outline"} onClick={() => setMode("range")}>{t("dateRange") || "Date Range"}</Button>
          </div>
          
          {mode === "single" ? (
            <div className="space-y-2">
              <Label>Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start">
                    <CalendarIcon className="mr-2 h-4 w-4" />{format(date, 'PPP')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} />
                </PopoverContent>
              </Popover>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t("startDate")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-sm">
                        <CalendarIcon className="mr-2 h-4 w-4" />{format(date, 'PP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>{t("endDate")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-sm">
                        <CalendarIcon className="mr-2 h-4 w-4" />{format(endDate, 'PP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar mode="single" selected={endDate} onSelect={(d) => d && setEndDate(d)} />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("excludeDays") || "Exclude Days"}</Label>
                <div className="flex flex-wrap gap-2">
                  {dayNames.map((day, i) => (
                    <Button key={i} size="sm" variant={excludedDays.includes(i) ? "destructive" : "outline"} onClick={() => toggleExcludedDay(i)} className="text-xs">{day}</Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">{t("selectDaysToExclude") || "Click days to exclude"}</p>
              </div>
            </>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t("startTime") || "Start Time"}</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>{t("endTime") || "End Time"}</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel") || "Cancel"}</Button>
          <Button onClick={handleSubmit}>{t("assignShift") || "Assign"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignShiftDialog;
